import type { Map } from 'leaflet'
import { MAX_ZOOM, MIN_ZOOM } from '~/helpers/maps'

type MapCallback = (map: Map) => void

type UseMap = {
  withMap: (cb: MapCallback) => void
  isRetina: Ref<boolean>
  zoomIn: () => void
  zoomOut: () => void
}

type UseMapOptions = {
  noTouch?: boolean
  init?: MapCallback
}

export default function (
  el: Ref<HTMLElement | null | undefined>,
  options: UseMapOptions = {},
): UseMap {
  // Options defined by plugins without type definitions.
  /* eslint-disable  @typescript-eslint/no-explicit-any */
  const opts: any = {
    smoothWheelZoom: true,
    smoothSensitivity: 1.3,
  }

  let map: Map | null = null

  const config = useRuntimeConfig()
  const mapReady = ref(false)
  const mapInitialised = ref(false)
  const isRetina = ref(false)

  function getSourceUrl(isRetina = false) {
    const tiles = isRetina ? 'zt_web_editorial_hq' : 'zt_web_editorial'
    const key = config.public.mapzApiKey
    return `https://tiles.mapz.com/mapproxy/v1/${key}/tiles/1.0.0/${tiles}/EPSG3857/{z}/{x}/{-y}.jpeg`
  }

  async function initMap(mapElement: HTMLElement) {
    if (mapInitialised.value) {
      return
    }
    mapInitialised.value = true
    const L = await import('leaflet')
    map = new L.Map(mapElement, {
      renderer: L.canvas(),
      attributionControl: false,
      scrollWheelZoom: true,
      dragging: !options.noTouch,
      touchZoom: true,
      zoomControl: false,
      ...opts,
    })

    const currentYear = new Date().getFullYear().toString()
    isRetina.value = L.Browser?.retina || false

    L.tileLayer(getSourceUrl(isRetina.value), {
      attribution: `© ${currentYear} <a target="_blank" href="http://www.mapz.com">mapz.com </a> - Map Data: <a target="_blank" href="http://openstreetmap.org">OpenStreetMap</a> (<a href="http://opendatacommons.org/licenses/odbl/1.0/" target="_blank">ODbL</a>)`,
      maxZoom: MAX_ZOOM,
      minZoom: MIN_ZOOM,
      // tms: true,
      tms: false,
    }).addTo(map)

    L.control
      .attribution({
        position: 'bottomleft',
      })
      .addTo(map)

    if (options.init) {
      options.init(map)
    }

    mapReady.value = true
  }

  onMounted(() => {
    // Init the map on mounted.
    if (el.value) {
      initMap(el.value)
    }
  })

  onBeforeUnmount(() => {
    if (!map) {
      return
    }

    map.off()
    map.remove()
    map = null
  })

  // If on mounted the element is not available, wait until it's available.
  watch(el, function (mapElement) {
    if (mapElement) {
      initMap(mapElement)
    }
  })

  return {
    isRetina,
    withMap: (cb: MapCallback) => {
      if (map) {
        cb(map)
      }
    },

    zoomIn: () => {
      if (map) {
        map.zoomIn()
      }
    },

    zoomOut: () => {
      if (map) {
        map.zoomOut()
      }
    },
  }
}
